/**
 * @file useAttachmentVersionHistory.ts
 * @description Every version an expanded attachment strip should list, newest
 * first, keyed by fileUid. Consumer: `AttachmentList`.
 *
 * The artifact document is not a history: a winning save deletes its sibling
 * saves from it and a losing save never lands there, so it holds every
 * published version plus at most one save. `attachments/{fileUid}` keeps them
 * all, so an expanded strip reads that document and merges it under the
 * artifact map — the artifact map wins on collision, because `currentVersionId`
 * and the publish action resolve against it (SYD-2455).
 *
 * One attachment can be referenced by several artifacts, so a fetched version
 * is listed only when its `artifactReferences` names this artifact.
 *
 * Two things invalidate a file's read, and nothing else does: collapsing it,
 * which drops the cached versions so the next expansion reads afresh, and the
 * artifact map's own set of version ids changing while the file is expanded,
 * which is what a delete, a publish or an upload does. Both are deliberate user
 * actions, so the read count stays at one per action rather than one per render
 * — the cache is keyed on those ids, so a new `attachments` identity carrying
 * the same ids reads nothing. A collapsed file is never read for, and a response
 * that lands after its key has moved on is discarded.
 */

import { useEffect, useRef, useState } from 'react';

import { ArtifactAttachments, AttachmentVersion, AttachmentVersionReference } from '#types/System.types';
import { readAttachmentDocument } from '../../services/readAttachmentDocument';

/** One file's strip, and whether the panel may claim it is the whole story. */
export type AttachmentVersionHistory = {
    /** The versions to list, newest first. */
    versions: [string, AttachmentVersion][];
    /**
     * Whether `attachments/{fileUid}` has been read for this file, so `versions`
     * is everything this artifact lists. False for a collapsed file, which is
     * never read for, and false while a read is in flight.
     */
    documentRead: boolean;
};

/** The artifact map's version ids for one file, as the key a read is cached under. */
const artifactVersionKey = (attachmentEntry: ArtifactAttachments[string] | undefined): string =>
    Object.keys(attachmentEntry?.versions ?? {}).sort().join('|');

export const useAttachmentVersionHistory = (
    artifactPath: string,
    attachments: ArtifactAttachments,
    expanded: Record<string, boolean>,
): Record<string, AttachmentVersionHistory> => {
    const [fetchedVersions, setFetchedVersions] =
        useState<Record<string, Record<string, AttachmentVersionReference>>>({});
    const readKeys = useRef<Record<string, string>>({});

    useEffect(() => {
        Object.entries(expanded).forEach(([fileUid, isExpanded]) => {
            if (!isExpanded) {
                if (readKeys.current[fileUid] === undefined) return;
                delete readKeys.current[fileUid];
                setFetchedVersions((previous) => {
                    if (previous[fileUid] === undefined) return previous;
                    const remaining = { ...previous };
                    delete remaining[fileUid];
                    return remaining;
                });
                return;
            }

            const readKey = artifactVersionKey(attachments[fileUid]);
            if (readKeys.current[fileUid] === readKey) return;
            readKeys.current[fileUid] = readKey;

            void readAttachmentDocument(fileUid).then((attachment) => {
                if (readKeys.current[fileUid] !== readKey) return;
                setFetchedVersions((previous) => ({ ...previous, [fileUid]: attachment?.versions ?? {} }));
            });
        });
    }, [expanded, attachments]);

    return Object.fromEntries(
        Object.entries(attachments).map(([fileUid, attachmentEntry]) => {
            const artifactVersions = attachmentEntry.versions ?? {};
            const fetched = fetchedVersions[fileUid];
            const referencedVersions = Object.entries(fetched ?? {})
                .filter(([, version]) => (version.artifactReferences ?? {})[artifactPath] !== undefined);

            return [
                fileUid,
                {
                    versions: Object.entries<AttachmentVersion>({
                        ...Object.fromEntries(referencedVersions),
                        ...artifactVersions,
                    }).sort(([, aVersion], [, bVersion]) => bVersion.versionNumber - aVersion.versionNumber),
                    documentRead: fetched !== undefined,
                },
            ];
        }),
    );
};
